import { Button } from "@shared/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@shared/components/ui/dropdown-menu"
import { cn } from "@shared/lib/utils"
import { MoreHorizontal } from "lucide-react"
import type { TableAction, TableActionGroup } from "@shared/types/data-table"

interface ActionDropdownProps<T> {
  /** Array of actions or grouped actions to display */
  actions: TableAction<T>[] | TableActionGroup<T>[]
  /** The entity item to pass to action handlers */
  item: T
  /** Optional custom class name for the trigger button */
  triggerClassName?: string
  /** Button variant - 'ghost' for tables, 'outline' for panels */
  variant?: 'ghost' | 'outline'
}

/**
 * Type guard to check if actions are grouped
 */
function isGroupedActions<T>(
  actions: TableAction<T>[] | TableActionGroup<T>[]
): actions is TableActionGroup<T>[] {
  return actions.length > 0 && 'label' in actions[0] && 'actions' in actions[0]
}

/**
 * Generic action dropdown component
 * Used in tables, panels, and anywhere actions are needed
 *
 * @example
 * ```tsx
 * const actions = getCustomerActions(handlers)
 * <ActionDropdown actions={actions.rowActions} item={customer} />
 * ```
 */
export function ActionDropdown<T>({
  actions,
  item,
  triggerClassName,
  variant = 'ghost'
}: ActionDropdownProps<T>) {
  if (!actions.length) return null

  /**
   * Filters individual action based on conditions and disabled state
   */
  const filterAction = (action: TableAction<T>): boolean => {
    // Check condition function
    if (action.condition && !action.condition(item)) return false

    // Check disabled state
    if (typeof action.disabled === 'function') {
      if (action.disabled(item)) return false
    } else if (action.disabled === true) {
      return false
    }

    return true
  }

  /**
   * Renders a single action menu item
   */
  const renderAction = (action: TableAction<T>) => {
    if (!filterAction(action)) return null

    return (
      <DropdownMenuItem
        key={action.key}
        onClick={() => action.onClick(item)}
        className={cn(
          action.variant === "destructive" &&
          "text-red-600 hover:text-red-600 focus:text-red-600"
        )}
      >
        {action.label}
      </DropdownMenuItem>
    )
  }

  const grouped = isGroupedActions(actions)

  // For grouped actions, compute visible groups (groups with at least one visible action)
  const visibleGroups = grouped
    ? actions
        .map(group => ({
          ...group,
          actions: group.actions.filter(filterAction),
        }))
        .filter(group => group.actions.length > 0)
    : null

  // Don't render if there are no visible actions
  const hasVisibleActions = grouped
    ? visibleGroups!.length > 0
    : actions.some(a => filterAction(a as TableAction<T>))

  if (!hasVisibleActions) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'outline' ? (
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-9 w-9 p-0 border border-input bg-background rounded-full shadow-sm hover:bg-accent",
              "focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
              triggerClassName
            )}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-7 w-7 rounded-lg border border-transparent transition-all duration-200",
              "hover:bg-white hover:border-sidebar-border hover:shadow-sm",
              "data-[state=open]:bg-white data-[state=open]:border-sidebar-border data-[state=open]:shadow-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              triggerClassName
            )}
          >
            <MoreHorizontal className="h-2.5 w-2.5" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {visibleGroups ? (
          // Render grouped actions with labels, skipping empty groups
          visibleGroups.map((group, index) => (
            <div key={group.label}>
              {index > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
              {group.actions.map(action => (
                <DropdownMenuItem
                  key={action.key}
                  onClick={() => action.onClick(item)}
                  className={cn(
                    action.variant === "destructive" &&
                    "text-red-600 hover:text-red-600 focus:text-red-600"
                  )}
                >
                  {action.label}
                </DropdownMenuItem>
              ))}
            </div>
          ))
        ) : (
          // Render flat list of actions
          (actions as TableAction<T>[]).map(renderAction)
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
